// TDS → Transactions: the quarter's salary-TDS rows.
//
// These are STORED, not derived. Teamlio works salary TDS out from approved
// payroll for the challan cycle, but a branch whose payroll ran somewhere
// else has figures that have to be brought in — so Import hands out a
// template, reads the filled sheet back, and keeps the rows. Stored is also
// what makes Edit and Delete possible; a derived row has nothing to edit.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { tdsApi, ImportResult, TdsSalaryEntry, TdsSalaryEntryInput } from "../../api/tds";
import { EMPTY_FILTERS, TdsFilters, matchesFilters } from "./TdsFilterBar";
import TdsRowMenu, { ROW_MENU_CSS } from "./TdsRowMenu";

const inr = (n: number) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/** DD-MM-YYYY, the template's own format — not the browser's locale, which
 *  is how a day and a month swap places between two people. */
const showDate = (iso: string | null) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}-${m}-${y}` : iso;
};

export const TRANSACTION_CSS = `
.tds-tx-bar {
  display: flex; gap: 10px; flex-wrap: wrap;
  align-items: center; justify-content: flex-end;
}
.tds-tx-bar .tds-tx-month { margin-right: auto; }

/* gt-action-btn, the app's own. An earlier version drew its own button and
   filled it with var(--gt-primary) — #0f2d1f, a green dark enough to read
   as a black tile at that size. The theme's CTA is a pill at the size that
   colour was chosen for. */
.tds-tx-btn { font-size: 13px; padding: 9px 16px; white-space: nowrap; }
.tds-tx-btn svg { width: 16px; height: 16px; flex-shrink: 0; }
.tds-tx-btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
.tds-tx-btn:disabled:hover { transform: none; box-shadow: none; }

/* ---- the import dialog's own bits ----
   The dialog itself is the app's <Dialog>, which portals to <body>. The
   first version drew its own fixed overlay inside the page, which put it
   inside a scrolling card AND painted it with var(--gt-surface) -- a token
   this theme does not have (it is --gt-surface-0), so the panel had no
   background at all and the page showed straight through it. */
.tds-modal-step { margin-top: 18px; }
.tds-modal-step-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
.tds-modal-step-n {
  width: 20px; height: 20px; border-radius: 999px; flex-shrink: 0;
  background: var(--gt-primary); color: #fff; font-size: 11px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}

.tds-drop {
  margin: 10px 0 0 28px; padding: 24px 16px; text-align: center;
  border: 2px dashed var(--gt-border); border-radius: 12px;
  background: var(--gt-surface-2); cursor: pointer;
  transition: border-color 120ms, background 120ms;
}
.tds-drop:hover, .tds-drop.is-over { border-color: var(--gt-primary); background: var(--gt-primary-container); }
.tds-drop-main { font-size: 13px; font-weight: 600; color: var(--gt-text-secondary); }
.tds-drop-sub { font-size: 11.5px; color: var(--gt-text-tertiary); margin-top: 4px; }

/* ---- the row menu ---- */
${ROW_MENU_CSS}
.tds-row-item.is-danger:hover { background: #fee2e2; color: #991b1b; }

`;

/** Closes a popover on an outside click or Escape. */
const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.slice(result.indexOf(",") + 1);
      if (base64) resolve(base64);
      else reject(new Error("That file came back empty."));
    };
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

function ImportDialog({ financialYear, quarter, onClose, onImported }: {
  financialYear: string;
  quarter: number;
  onClose: () => void;
  onImported: (result: ImportResult) => void;
}) {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const onDownload = async () => {
    setDownloading(true);
    try {
      await tdsApi.downloadSalaryTemplate(financialYear, quarter);
      toast.success("Template downloaded — fill it in and drop it below.");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not build the template.");
    } finally {
      setDownloading(false);
    }
  };

  const take = async (file: File | undefined) => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error("Drop the filled-in .xlsx template.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await tdsApi.importSalaryEntries({
        financial_year: financialYear,
        quarter,
        file_name: file.name,
        data_base64: await readAsBase64(file),
        replace: true,
      });
      onImported(data);
      onClose();
    } catch (e: any) {
      // The server names the offending row and column; show that, not a
      // generic failure — it is the whole reason the check is there.
      toast.error(e?.response?.data?.detail || e?.message || "Could not read that file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import salary entries — {financialYear} Q{quarter}</DialogTitle>
        </DialogHeader>

        <div className="tds-modal-step" style={{ marginTop: 4 }}>
          <div className="tds-modal-step-head">
            <span className="tds-modal-step-n">1</span> Download the sample
          </div>
          <div style={{ margin: "10px 0 0 28px" }}>
            <Button variant="outline" onClick={onDownload} disabled={downloading}>
              <Download style={{ width: 16, height: 16, marginRight: 6 }} />
              {downloading ? "Building…" : "Download sample"}
            </Button>
          </div>
        </div>

        <div className="tds-modal-step">
          <div className="tds-modal-step-head">
            <span className="tds-modal-step-n">2</span> Drop the filled-in file here
          </div>
          <div
            className={`tds-drop${over ? " is-over" : ""}`}
            onClick={() => picker.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              take(e.dataTransfer.files?.[0]);
            }}
          >
            <div className="tds-drop-main">
              <Upload style={{ width: 18, height: 18, verticalAlign: "-3px", marginRight: 6 }} />
              {busy ? "Reading…" : "Drag the .xlsx here, or click to pick it"}
            </div>
            <div className="tds-drop-sub">Only the filled-in template is accepted.</div>
          </div>
          <input ref={picker} type="file" accept=".xlsx" style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; take(f); }} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FIELDS: { key: keyof TdsSalaryEntryInput; label: string;
                 kind: "text" | "number" | "date" | "yesno" | "deductee" }[] = [
  { key: "employee_code", label: "Employee Code", kind: "text" },
  { key: "employee_name", label: "Employee Name", kind: "text" },
  { key: "pan", label: "PAN", kind: "text" },
  { key: "paid_on", label: "Credit Date", kind: "date" },
  { key: "deducted_on", label: "Deduction Date", kind: "date" },
  { key: "section", label: "Section", kind: "text" },
  // Not on the import template -- that is a copy of the department's own
  // sheet, verified column for column -- so rows arrive resident and are
  // corrected here. It matters because the portal takes a separate challan
  // for each.
  { key: "deductee_type", label: "Deductee Type", kind: "deductee" },
  { key: "amount_paid", label: "Amt Paid", kind: "number" },
  { key: "tds", label: "TDS", kind: "number" },
  { key: "surcharge", label: "Sur.", kind: "number" },
  { key: "education_cess", label: "H.E. Cess", kind: "number" },
  { key: "health_cess", label: "H. Cess", kind: "number" },
  { key: "total", label: "Total", kind: "number" },
  { key: "us_192_1a", label: "u/s 192(1A)", kind: "yesno" },
];

function EditDialog({ entry, onClose, onSaved }: {
  entry: TdsSalaryEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<TdsSalaryEntryInput>({
    employee_code: entry.employee_code,
    employee_name: entry.employee_name,
    pan: entry.pan,
    paid_on: entry.paid_on,
    deducted_on: entry.deducted_on,
    section: entry.section,
    // Rows that predate the column read as resident, same as the default.
    deductee_type: entry.deductee_type || "resident",
    amount_paid: entry.amount_paid,
    tds: entry.tds,
    surcharge: entry.surcharge,
    education_cess: entry.education_cess,
    health_cess: entry.health_cess,
    total: entry.total,
    us_192_1a: entry.us_192_1a,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.employee_name?.trim()) { toast.error("An employee name is needed."); return; }
    setSaving(true);
    try {
      await tdsApi.updateSalaryEntry(entry.id, form);
      toast.success("Transaction updated");
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit transaction</DialogTitle>
          <DialogDescription>
            The same columns as the template. Leave Total at 0 to have it added up
            from the tax columns.
          </DialogDescription>
        </DialogHeader>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0 12px" }}>
          {FIELDS.map((field) => (
            <label key={field.key} style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 10 }}>
              {field.label}
              {field.kind === "deductee" ? (
                <select
                  className="gt-input"
                  value={(form.deductee_type as string) || "resident"}
                  style={{ width: "100%", marginTop: 4, fontWeight: 400 }}
                  onChange={(e) => setForm((f) => ({ ...f, deductee_type: e.target.value }))}
                >
                  <option value="resident">Resident</option>
                  <option value="nonResident">Non-Resident</option>
                </select>
              ) : field.kind === "yesno" ? (
                <select
                  className="gt-input"
                  value={form.us_192_1a ? "Yes" : "No"}
                  style={{ width: "100%", marginTop: 4, fontWeight: 400 }}
                  onChange={(e) => setForm((f) => ({ ...f, us_192_1a: e.target.value === "Yes" }))}
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>
              ) : (
                <input
                  className="gt-input"
                  type={field.kind === "number" ? "number" : field.kind === "date" ? "date" : "text"}
                  value={(form[field.key] ?? "") as string | number}
                  style={{ width: "100%", marginTop: 4, fontWeight: 400 }}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    [field.key]: field.kind === "number" ? Number(e.target.value || 0) : e.target.value,
                  }))}
                />
              )}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TdsTransactions({
  financialYear,
  quarter,
  filters = EMPTY_FILTERS,
}: {
  financialYear: string;
  quarter: number;
  filters?: TdsFilters;
}) {
  const [all, setAll] = useState<TdsSalaryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState<TdsSalaryEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await tdsApi.salaryEntries(financialYear, quarter);
      setAll(data.entries);
      setError(null);
    } catch (e: any) {
      setAll([]);
      setError(e?.response?.data?.detail || e?.message || "Couldn't load this quarter's transactions.");
    } finally {
      setLoading(false);
    }
  }, [financialYear, quarter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (entry: TdsSalaryEntry) => {
    if (!window.confirm(`Delete ${entry.employee_name}'s ${showDate(entry.paid_on)} transaction?`)) return;
    try {
      await tdsApi.deleteSalaryEntry(entry.id);
      toast.success("Transaction deleted");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not delete that.");
    }
  };

  // The same workbook the template is, filled in -- not a CSV. A CSV loses
  // the sheet the importer looks for and the text formatting that keeps a
  // date a date, so it could be read by a person but not fed back in.
  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    if (!entries.length) {
      toast.error(emptyBecauseFiltered
        ? "Nothing matches the filter, so there is nothing to export."
        : "Nothing to export yet.");
      return;
    }
    setExporting(true);
    try {
      await tdsApi.downloadSalaryEntries(financialYear, quarter,
        { month: filters.month || undefined, section: filters.section || undefined });
      toast.success(`${entries.length} transaction${entries.length === 1 ? "" : "s"} exported`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not build that file.");
    } finally {
      setExporting(false);
    }
  };

  // What the bar is showing. The quarter is fetched whole and narrowed
  // here, so changing the month does not cost a round trip.
  const entries = useMemo(
    () => all.filter((e) => matchesFilters(e, filters)), [all, filters]);

  // Added up from the rows ON SCREEN, not taken from the server's figure
  // for the whole quarter: a footer that totals rows the table is not
  // showing is a number nobody can check by reading up the column.
  const totals = useMemo(() => entries.reduce(
    (acc, e) => ({
      amount_paid: acc.amount_paid + (e.amount_paid || 0),
      tds: acc.tds + (e.tds || 0),
      total: acc.total + (e.total || 0),
    }),
    { amount_paid: 0, tds: 0, total: 0 },
  ), [entries]);

  // A count, not a sum: the column is a flag, so "how many rows carry it"
  // is the only total it has.
  const perquisiteRows = useMemo(
    () => entries.filter((e) => e.us_192_1a).length, [entries]);

  // Nothing imported and nothing matching are different things, and an
  // empty table that says "import the template" when the quarter is full
  // sends somebody off to re-upload a file they already have.
  const hidden = all.length - entries.length;
  const emptyBecauseFiltered = !entries.length && all.length > 0;

  return (
    <div className="space-y-4">
      <style>{TRANSACTION_CSS}</style>

      <div className="tds-tx-bar">
        <p className="gt-page-sub tds-tx-month" style={{ fontSize: 11.5, margin: 0, color: "var(--gt-text-tertiary)" }}>
          {entries.length
            ? `${entries.length} transaction${entries.length === 1 ? "" : "s"} for ${financialYear} Q${quarter}`
              + (hidden ? ` · ${hidden} more hidden by the filter` : "")
            : emptyBecauseFiltered
              ? `None of this quarter's ${all.length} transaction${all.length === 1 ? "" : "s"} match the filter.`
              : `No transactions for ${financialYear} Q${quarter} yet — import the template to add them.`}
        </p>

        <button type="button" className="gt-action-btn tds-tx-btn" onClick={() => setImporting(true)}>
          <Download /> Import
        </button>
        <button type="button" className="gt-action-btn is-ghost tds-tx-btn"
          onClick={onExport} disabled={!entries.length || exporting}
          title="These transactions in the template's own format — edit it and import it straight back.">
          <Upload /> {exporting ? "Building…" : "Export"}
        </button>
      </div>

      {error && <div className="gt-card p-5" style={{ color: "#b91c1c", fontSize: 12.5 }}>{error}</div>}

      {!error && (
        <div className="gt-card p-0">
          <div style={{ overflow: "auto" }}>
            <table style={{ width: "100%", minWidth: 1000, fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--gt-surface-2)" }}>
                  {["Code", "Employee", "PAN", "Date", "Sec."].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: 8, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                  {["Amt Paid", "TDS", "Sur.", "H.E. Cess", "H. Cess", "Total", "u/s 192(1A)"].map((h) => (
                    <th key={h} style={{ textAlign: "right", padding: 8, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                  <th style={{ padding: 8, width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {!entries.length && (
                  <tr>
                    <td colSpan={13} style={{ padding: 20, textAlign: "center", color: "var(--gt-text-tertiary)" }}>
                      {loading
                        ? "Loading…"
                        : emptyBecauseFiltered
                          ? "Nothing matches the month and section above. Reset the filter to see the quarter."
                          : "Nothing here yet. Import the filled-in template to add transactions."}
                    </td>
                  </tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} style={{ borderTop: "1px solid var(--gt-border)" }}>
                    <td style={{ padding: 8 }}>{e.employee_code || "—"}</td>
                    <td style={{ padding: 8 }}>{e.employee_name}</td>
                    {/* A missing PAN is the one thing here that stops a
                        return, so it is called out rather than left blank. */}
                    <td style={{ padding: 8, fontFamily: "var(--gt-font-mono)" }}>
                      {e.pan || <span style={{ color: "#b91c1c" }}>no PAN</span>}
                    </td>
                    <td style={{ padding: 8, whiteSpace: "nowrap" }}>{showDate(e.paid_on)}</td>
                    <td style={{ padding: 8 }}>{e.section}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(e.amount_paid)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(e.tds)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(e.surcharge)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(e.education_cess)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(e.health_cess)}</td>
                    <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>{inr(e.total)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{e.us_192_1a ? "Yes" : "No"}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <TdsRowMenu items={[
                        { label: "Edit", onSelect: () => setEditing(e) },
                        { label: "Delete", danger: true, onSelect: () => remove(e) },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
              {entries.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: "2px solid var(--gt-border)", fontWeight: 700 }}>
                    <td colSpan={5} style={{ padding: 8 }}>
                      {entries.length} transaction{entries.length === 1 ? "" : "s"}
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(totals.amount_paid)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(totals.tds)}</td>
                    <td colSpan={3} />
                    <td style={{ padding: 8, textAlign: "right" }}>{inr(totals.total)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{perquisiteRows || "—"}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {importing && (
        <ImportDialog
          financialYear={financialYear}
          quarter={quarter}
          onClose={() => setImporting(false)}
          onImported={(r) => {
            // Says what it replaced as well as what it added. The file only
            // speaks for its own months, and the filer should be able to see
            // that from the message rather than by counting rows afterwards.
            const added = `${r.imported} transaction${r.imported === 1 ? "" : "s"} imported`;
            const where = r.months ? ` for ${r.months}` : "";
            if (r.replaced) {
              const edited = r.replaced_edited
                ? `, ${r.replaced_edited} of which you had edited by hand`
                : "";
              toast.success(`${added}${where} — replaced ${r.replaced} earlier row${r.replaced === 1 ? "" : "s"}${edited}.`);
            } else {
              toast.success(`${added}${where}.`);
            }
            load();
          }}
        />
      )}

      {editing && (
        <EditDialog entry={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
